/**
 * Reusable Tanstack-powered data table.
 * - Sortable headers (click to toggle asc/desc)
 * - Sticky thead when scrolling inside a card
 * - Row click → onRowClick(row.original)
 * - Uses the .data-table CSS utility for typography + accent palette
 * - Optional `header` slot rendered above the table, inside the same card.
 *
 * Column shape (column-def subset we use):
 *   { accessorKey, header, cell, size?, enableSorting?, meta: { align, className } }
 */
import React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";

export function DataTable({
  columns,
  data,
  onRowClick,
  selectedRowId,           // string | number — matches row.original.id
  getRowTestId,            // (row) => string
  emptyMessage = "No rows",
  initialSorting = [],
  maxHeight = "calc(100vh - 320px)",
  header,                  // optional ReactNode rendered above the table
  className = "card-surface overflow-hidden",
  "data-testid": testId,
}) {
  const [sorting, setSorting] = React.useState(initialSorting);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className={className} data-testid={testId}>
      {header && (
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          {header}
        </div>
      )}
      <div className="overflow-auto" style={{ maxHeight }}>
        <table className="data-table">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const canSort = h.column.getCanSort();
                  const sort = h.column.getIsSorted(); // false | "asc" | "desc"
                  const align = h.column.columnDef.meta?.align; // "right" | "center"
                  return (
                    <th
                      key={h.id}
                      data-sortable={canSort ? "true" : "false"}
                      className={align || ""}
                      style={{ width: h.getSize() !== 150 ? h.getSize() : undefined }}
                      onClick={canSort ? h.column.getToggleSortingHandler() : undefined}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {canSort && (
                        <span className="sort-ind">
                          {sort === "asc"  && <ArrowUp   size={10} />}
                          {sort === "desc" && <ArrowDown size={10} />}
                          {sort === false  && <ChevronsUpDown size={10} style={{ opacity: 0.35 }} />}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isSelected = selectedRowId !== undefined && row.original.id === selectedRowId;
              return (
                <tr
                  key={row.id}
                  className={`${onRowClick ? "clickable" : ""} ${isSelected ? "selected" : ""}`}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  data-testid={getRowTestId ? getRowTestId(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const align = cell.column.columnDef.meta?.align;
                    const cls   = cell.column.columnDef.meta?.className || "";
                    return (
                      <td key={cell.id} className={`${align || ""} ${cls}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="text-center py-10 meta">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
